import { useLazyQuery } from '@apollo/client/react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNotify } from 'react-admin';

import {
  ClientConfigurationIssuesDocument,
  ClientLaunchType,
} from '../../../graphql/types';
import { BORDER_GRAY } from '../../../theme';
import { ConfigurationIssueItem } from '../components/ConfigurationIssueItem';
import { REVIEW_STEP_ID } from './launchState';
import type { ClientLaunch, StepProps } from './types';

type ReviewProps = StepProps & { launch: ClientLaunch };

const Section: React.FC<{
  title: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ title, hint, children }) => (
  <Stack spacing={1.5}>
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.disabled">
          {hint}
        </Typography>
      )}
    </Box>
    {children}
  </Stack>
);

const Summary: React.FC<ReviewProps> = ({
  launch,
  readOnly,
  steps,
  statuses,
  goToStep,
}) => (
  <Section
    title={
      launch.type === ClientLaunchType.FeatureLaunch
        ? 'Feature launch'
        : 'Summary'
    }
  >
    <Box sx={{ border: `1px solid ${BORDER_GRAY}`, borderRadius: 2 }}>
      {steps
        .filter((step) => step.id !== REVIEW_STEP_ID)
        .map((step, index) => {
          const status = statuses[steps.indexOf(step)];
          return (
            <Box
              key={step.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', sm: '170px 1fr auto' },
                gap: 1.5,
                alignItems: 'baseline',
                px: 1.75,
                py: 1.25,
                borderTop: index ? `1px solid ${BORDER_GRAY}` : undefined,
              }}
            >
              <Typography variant="body2" color="text.disabled">
                {step.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  gridRow: { xs: 2, sm: 'auto' },
                  overflowWrap: 'anywhere',
                }}
              >
                {step.summary?.(launch) ??
                  (status.saved ? 'Saved' : 'Not saved yet')}
              </Typography>
              <Button
                size="small"
                disabled={readOnly}
                onClick={() => goToStep(step.id)}
              >
                Edit
              </Button>
            </Box>
          );
        })}
    </Box>
  </Section>
);

const ClientDiagnostics: React.FC<ReviewProps> = ({
  launch,
  readOnly,
  diagnostics,
  onDiagnosticsStart,
  onDiagnosticsRun,
}) => {
  const notify = useNotify();
  const [running, setRunning] = React.useState(false);
  const [fetchIssues] = useLazyQuery(ClientConfigurationIssuesDocument, {
    fetchPolicy: 'network-only',
  });

  const runDiagnostics = async () => {
    setRunning(true);
    onDiagnosticsStart();
    try {
      const { data } = await fetchIssues({
        variables: { id: launch.client.id },
      });
      if (!data?.client) {
        throw new Error('the client was not found');
      }
      onDiagnosticsRun(data.client.configurationIssues);
    } catch (error) {
      notify(
        `Failed to run diagnostics: ${error instanceof Error ? error.message : String(error)}`,
        { type: 'error' },
      );
    } finally {
      setRunning(false);
    }
  };

  const clean = diagnostics.kind === 'done' && !diagnostics.issues.length;

  return (
    <Section title="Client diagnostics" hint="Required before finishing.">
      <Box>
        <Button
          size="small"
          variant={clean ? 'outlined' : 'contained'}
          disabled={readOnly || running}
          onClick={runDiagnostics}
        >
          {diagnostics.kind === 'notRun'
            ? 'Run diagnostics'
            : 'Run diagnostics again'}
        </Button>
      </Box>
      {diagnostics.kind === 'stale' && (
        <Alert severity="warning">
          Setup changed since the last run. Run diagnostics again.
        </Alert>
      )}
      {clean && (
        <Alert severity="success">No configuration issues found.</Alert>
      )}
      {diagnostics.kind === 'done' &&
        diagnostics.issues.map((issue) => (
          <ConfigurationIssueItem issue={issue} key={issue.title} />
        ))}
    </Section>
  );
};

export const ReviewStep: React.FC<StepProps> = ({ launch, ...props }) => {
  // Review comes after step 1, so the launch always exists by then.
  if (!launch) {
    return null;
  }
  return (
    <Stack spacing={3}>
      <Summary launch={launch} {...props} />
      <ClientDiagnostics launch={launch} {...props} />
      {launch.type === ClientLaunchType.FeatureLaunch && (
        <Typography variant="body2" color="text.disabled">
          Features can stop at test users or some gyms. Widen them in a later
          launch when you're ready.
        </Typography>
      )}
    </Stack>
  );
};
